/**
 * Build a Google Calendar "add event" URL for a grant deadline.
 * Users click → Google Calendar opens pre-filled → they save with one click.
 * No OAuth required. Works on desktop + mobile.
 */

function pad(n) { return String(n).padStart(2, '0'); }

function formatGCalDate(date) {
  // Google Calendar expects YYYYMMDDTHHMMSSZ (UTC)
  const y = date.getUTCFullYear();
  const m = pad(date.getUTCMonth() + 1);
  const d = pad(date.getUTCDate());
  const h = pad(date.getUTCHours());
  const min = pad(date.getUTCMinutes());
  const s = pad(date.getUTCSeconds());
  return `${y}${m}${d}T${h}${min}${s}Z`;
}

/**
 * Parse a grant deadline string into a Date. Returns null for "Rolling",
 * empty strings, or unparseable input.
 */
export function parseDeadline(deadline) {
  if (!deadline) return null;
  const s = String(deadline).trim();
  if (!s || s.toLowerCase() === 'rolling' || s.toLowerCase() === 'ongoing') return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return d;
}

/**
 * Generate a Google Calendar URL for a grant deadline.
 * Returns null if the deadline can't be parsed.
 */
export function googleCalendarUrl(grant) {
  const deadline = parseDeadline(grant.deadline);
  if (!deadline) return null;

  // Create a 30-min event at 9am on the deadline day
  const start = new Date(deadline);
  start.setUTCHours(13, 0, 0, 0); // 9am ET-ish
  const end = new Date(start.getTime() + 30 * 60 * 1000);

  const funder = grant.funder_name || 'Grant';
  const name = grant.grant_name || grant.name || 'Grant Deadline';
  const title = `Grant Deadline: ${name} (${funder})`;

  const details = [
    `Funder: ${funder}`,
    grant.funding_range ? `Amount: $${grant.funding_range.min?.toLocaleString()} – $${grant.funding_range.max?.toLocaleString()}` : null,
    grant.application_url ? `Apply: ${grant.application_url}` : null,
    grant.description ? `\n${grant.description}` : null,
    '\nTracked in RaiseRoot (https://raiseroot.co)',
  ].filter(Boolean).join('\n');

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    dates: `${formatGCalDate(start)}/${formatGCalDate(end)}`,
    details,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generate an ICS file blob for the deadline (fallback for non-Google calendars).
 */
export function icsBlob(grant) {
  const deadline = parseDeadline(grant.deadline);
  if (!deadline) return null;
  const start = new Date(deadline);
  start.setUTCHours(13, 0, 0, 0);
  const end = new Date(start.getTime() + 30 * 60 * 1000);
  const funder = grant.funder_name || 'Grant';
  const name = grant.grant_name || grant.name || 'Grant Deadline';
  const uid = `raiseroot-${grant.id || Date.now()}@raiseroot.co`;

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//RaiseRoot//Grant Deadline//EN',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatGCalDate(new Date())}`,
    `DTSTART:${formatGCalDate(start)}`,
    `DTEND:${formatGCalDate(end)}`,
    `SUMMARY:Grant Deadline: ${name} (${funder})`,
    `DESCRIPTION:Tracked in RaiseRoot`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return new Blob([ics], { type: 'text/calendar' });
}

export function downloadIcs(grant) {
  const blob = icsBlob(grant);
  if (!blob) return false;
  const name = (grant.grant_name || grant.name || 'grant').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}-deadline.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return true;
}
